import SFRDashboard from "@/components/SFRDashboard";
import { useEffect, useState, useCallback } from "react";
import {
  Shield, FileText, AlertTriangle, CheckCircle, Clock, ChevronDown, ChevronUp,
  Download, Plus, TrendingUp, BookOpen, AlertOctagon, Search, FileSearch,
  Activity, BarChart3, FlaskConical, Users, Send, Radio,
} from "lucide-react";
import { exportPDF, exportCSV, todayStr } from "@/utils/exportUtils";

const API = process.env.REACT_APP_BACKEND_URL;

async function apiFetch(path) {
  const token = localStorage.getItem("fs_token");
  const r = await fetch(`${API}/api/frms${path}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return r.json();
}

async function apiPost(path, body) {
  const token = localStorage.getItem("fs_token");
  const r = await fetch(`${API}/api/frms${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return r.json();
}

const RISK_COLOR = {
  HIGH:   "bg-red-500/10 text-red-400 border-red-500/30",
  MEDIUM: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  LOW:    "bg-green-500/10 text-green-400 border-green-500/30",
};

const STATUS_COLOR = {
  approved:   "text-green-400 bg-green-500/10 border-green-500/20",
  draft:      "text-amber-400 bg-amber-500/10 border-amber-500/20",
  "under review": "text-blue-400 bg-blue-500/10 border-blue-500/20",
};

const OCC_TYPE_OPTS = ["Fatigue Event","Fatigue Near Miss","Insufficient Rest","WOCL Impairment","Cumulative Fatigue","Medical Fitness Concern","Other"];
const FATIGUE_LEVELS = ["1 — Slight","2 — Moderate","3 — Significant","4 — Severe","5 — Incapacitating"];

// ─── Overview Tab ─────────────────────────────────────────────────────────────
function OverviewTab({ overview, onSelectTab }) {
  if (!overview) return null;
  const { checklist = [] } = overview;
  const gaugeAngle = (overview.compliance_score / 100) * 180;

  return (
    <div className="space-y-5">
      {/* KPIs */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: "Compliance Score",    value: `${overview.compliance_score}%`,     color: "text-green-400",  Icon: TrendingUp },
          { label: "Policy Documents",    value: overview.policy_documents,           color: "text-primary",    Icon: BookOpen },
          { label: "Hazards Registered",  value: overview.open_hazards + " open",     color: "text-amber-400",  Icon: AlertTriangle },
          { label: "Occurrences",         value: overview.occurrence_reports,         color: "text-blue-400",   Icon: FileSearch },
          { label: "Open Audit Findings", value: overview.open_audit_findings,        color: "text-orange-400", Icon: AlertOctagon },
          { label: "DGCA Readiness",      value: "Phase 3",                           color: "text-green-400",  Icon: Shield },
        ].map(({ label, value, color, Icon }) => (
          <div key={label} className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</span>
              <Icon size={13} className={color} />
            </div>
            <div className={`text-xl font-mono font-black ${color}`}>{value}</div>
          </div>
        ))}
      </div>

      {/* Compliance Checklist */}
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div className="text-xs font-black uppercase tracking-widest text-muted-foreground">DGCA CAR / ICAO Doc 9966 — Compliance Checklist</div>
          <div className={`text-sm font-mono font-black ${overview.compliance_score >= 90 ? "text-green-400" : "text-amber-400"}`}>{overview.compliance_score}% — {overview.dgca_readiness}</div>
        </div>
        <div className="p-4 space-y-3">
          {checklist.map(c => {
            const pct = Math.round((c.complete / c.items) * 100);
            return (
              <div key={c.category} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-foreground">{c.category}</span>
                  <span className="font-mono text-muted-foreground">{c.complete}/{c.items} · {pct}%</span>
                </div>
                <div className="h-2 bg-background/60 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${pct === 100 ? "bg-green-500" : pct >= 80 ? "bg-blue-500" : "bg-amber-500"}`}
                    style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Nav */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { tab: "policy",    label: "Policy Library",       icon: BookOpen,      desc: `${overview.policy_documents} documents` },
          { tab: "hazards",   label: "Hazard Register",      icon: AlertTriangle, desc: `${overview.open_hazards} open hazards` },
          { tab: "reports",   label: "Occurrence Reports",   icon: FileSearch,    desc: `${overview.occurrence_reports} filed` },
          { tab: "audit",     label: "Audit Trail",          icon: FileText,      desc: `${overview.open_audit_findings} open findings` },
        ].map(({ tab, label, icon: Icon, desc }) => (
          <button key={tab} onClick={() => onSelectTab(tab)}
            className="bg-card border border-border rounded-sm p-4 text-left hover:border-primary/40 transition-colors group">
            <Icon size={16} className="text-muted-foreground group-hover:text-primary transition-colors mb-2" />
            <div className="text-sm font-bold text-foreground">{label}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Policy Documents Tab ─────────────────────────────────────────────────────
function PolicyTab({ docs }) {
  const [search, setSearch] = useState("");
  const filtered = docs.filter(d =>
    !search || d.title.toLowerCase().includes(search.toLowerCase()) || d.category.toLowerCase().includes(search.toLowerCase())
  );
  const handleExport = () => exportPDF({
    title: "FRMS Policy Document Register",
    subtitle: `${filtered.length} documents · ${todayStr()}`,
    columns: [
      { header: "ID",       key: "id",           width: 18 },
      { header: "Category", key: "category",     width: 28 },
      { header: "Title",    key: "title",        width: 70 },
      { header: "Version",  key: "version",      width: 16 },
      { header: "Status",   key: "status",       width: 18 },
      { header: "Approved", key: "approved_date", width: 22 },
      { header: "Review",   key: "review_due",   width: 22 },
      { header: "Ref",      key: "regulatory_ref", width: 50 },
    ],
    rows: filtered,
    filename: `frms-policy-docs-${todayStr()}.pdf`,
    orientation: "landscape",
  });
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search documents..."
            className="w-full pl-7 pr-3 py-1.5 bg-background border border-border rounded-sm text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
        </div>
        <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-1.5 border border-border text-xs text-muted-foreground hover:text-foreground rounded-sm transition-colors">
          <Download size={11} /> Export
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {filtered.map(doc => (
          <div key={doc.id} className="bg-card border border-border rounded-sm p-4" data-testid={`policy-doc-${doc.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold text-muted-foreground font-mono">{doc.id}</span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 border rounded-sm bg-primary/10 text-primary border-primary/20">{doc.category}</span>
                </div>
                <div className="text-sm font-bold text-foreground">{doc.title}</div>
                <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{doc.description}</div>
              </div>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 border rounded-sm shrink-0 ${STATUS_COLOR[doc.status] || STATUS_COLOR.approved}`}>
                {doc.status.toUpperCase()}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1"><BookOpen size={8} /> {doc.pages} pages</span>
              <span>{doc.version}</span>
              <span className="flex items-center gap-1"><CheckCircle size={8} /> Approved {doc.approved_date}</span>
              <span className="flex items-center gap-1"><Clock size={8} /> Review {doc.review_due}</span>
            </div>
            <div className="mt-2 text-[10px] text-muted-foreground font-mono">{doc.regulatory_ref}</div>
            <div className="mt-2 text-[10px] text-muted-foreground">Approved by: {doc.approved_by}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Hazard Register Tab ───────────────────────────────────────────────────────
function HazardTab({ data }) {
  const { hazards = [], summary = {} } = data;
  const handleExport = () => exportPDF({
    title: "FRMS Fatigue Hazard Register",
    subtitle: `${hazards.length} hazards · ${todayStr()}`,
    columns: [
      { header: "ID",          key: "id",          width: 18 },
      { header: "Category",    key: "category",    width: 24 },
      { header: "Hazard",      key: "hazard",      width: 64 },
      { header: "Risk",        key: "risk_rating", width: 16 },
      { header: "Residual",    key: "residual_risk", width: 16 },
      { header: "Status",      key: "status",      width: 18 },
      { header: "Owner",       key: "owner",       width: 38 },
      { header: "Review Date", key: "review_date", width: 22 },
    ],
    rows: hazards,
    filename: `frms-hazard-register-${todayStr()}.pdf`,
    orientation: "landscape",
  });
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {Object.entries(summary).filter(([k]) => k !== "total").map(([k, v]) => (
            <div key={k} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border text-xs font-bold ${RISK_COLOR[k.toUpperCase()]}`}>
              <span>{v}</span><span>{k.toUpperCase()}</span>
            </div>
          ))}
        </div>
        <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-1.5 border border-border text-xs text-muted-foreground hover:text-foreground rounded-sm transition-colors">
          <Download size={11} /> Export
        </button>
      </div>
      <div className="space-y-3">
        {hazards.map(h => (
          <div key={h.id} className="bg-card border border-border rounded-sm p-4" data-testid={`hazard-${h.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-mono font-bold text-muted-foreground">{h.id}</span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 bg-secondary/40 text-muted-foreground rounded-sm">{h.category}</span>
                </div>
                <div className="text-sm font-bold text-foreground">{h.hazard}</div>
                <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{h.description}</div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {h.controls.map(c => (
                    <span key={c} className="text-[10px] bg-primary/8 border border-primary/15 text-primary/80 px-1.5 py-0.5 rounded-sm">{c}</span>
                  ))}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <span className={`text-[10px] font-bold px-2 py-1 rounded-sm border ${RISK_COLOR[h.risk_rating]}`}>
                  {h.risk_rating}
                </span>
                <div className="text-[10px] text-muted-foreground">Residual: <span className={`font-bold ${RISK_COLOR[h.residual_risk].split(" ")[1]}`}>{h.residual_risk}</span></div>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 text-[10px] text-muted-foreground">
              <span>Owner: {h.owner}</span>
              <span>Review: {h.review_date}</span>
              <span className={`font-bold ${h.status === "mitigated" ? "text-green-400" : "text-amber-400"}`}>{h.status.toUpperCase()}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Occurrence Reports Tab ───────────────────────────────────────────────────
function OccurrenceTab({ occurrences, onSubmitted }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    occurrence_date: new Date().toISOString().slice(0,10),
    occurrence_type: "",
    fatigue_level: "",
    description: "",
    contributing_factors: [],
    corrective_action: "",
    anonymous: true,
    reporter_name: "",
    reporter_role: "",
    flight_number: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const handleSubmit = async () => {
    if (!form.occurrence_type || !form.fatigue_level || !form.description) return;
    setSubmitting(true);
    const r = await apiPost("/occurrences", form);
    setSubmitting(false);
    if (r.success) {
      setSubmitted(r);
      setShowForm(false);
      onSubmitted();
    }
  };

  const STATUS_CFG = {
    submitted:      { cls: "bg-primary/10 text-primary border-primary/20", label: "SUBMITTED" },
    under_review:   { cls: "bg-amber-500/10 text-amber-400 border-amber-500/20", label: "UNDER REVIEW" },
    investigated:   { cls: "bg-blue-500/10 text-blue-400 border-blue-500/20", label: "INVESTIGATED" },
    closed:         { cls: "bg-green-500/10 text-green-400 border-green-500/20", label: "CLOSED" },
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{occurrences.length} occurrence reports filed — all submissions are confidential and non-punitive.</div>
        <button onClick={() => setShowForm(v => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-bold rounded-sm hover:bg-primary/90 transition-colors"
          data-testid="new-occurrence-btn">
          <Plus size={11} /> File Report
        </button>
      </div>

      {submitted && (
        <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-sm text-xs text-green-400">
          <CheckCircle size={12} /> Report submitted — Ref: {submitted.report?.ref_number}. A safety investigator will review within 72 hours.
        </div>
      )}

      {showForm && (
        <div className="bg-card border border-border rounded-sm p-5 space-y-4">
          <div className="text-sm font-bold text-foreground">New Occurrence Report</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1 block">Occurrence Type *</label>
              <select value={form.occurrence_type} onChange={e => setForm(p => ({...p, occurrence_type: e.target.value}))}
                className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
                <option value="">Select...</option>
                {OCC_TYPE_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1 block">Fatigue Level *</label>
              <select value={form.fatigue_level} onChange={e => setForm(p => ({...p, fatigue_level: e.target.value}))}
                className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
                <option value="">Select...</option>
                {FATIGUE_LEVELS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1 block">Occurrence Date *</label>
              <input type="date" value={form.occurrence_date} onChange={e => setForm(p => ({...p, occurrence_date: e.target.value}))}
                className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1 block">Flight Number (optional)</label>
              <input type="text" value={form.flight_number} onChange={e => setForm(p => ({...p, flight_number: e.target.value}))}
                placeholder="e.g. 6E-201"
                className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1 block">Description *</label>
            <textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} rows={3}
              placeholder="Describe the fatigue event and its context..."
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1 block">Suggested Corrective Action</label>
            <input type="text" value={form.corrective_action} onChange={e => setForm(p => ({...p, corrective_action: e.target.value}))}
              placeholder="Your suggestion for preventing recurrence..."
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={form.anonymous} onChange={e => setForm(p => ({...p, anonymous: e.target.checked}))} className="accent-primary" />
              Submit anonymously (recommended)
            </label>
            <div className="flex gap-2">
              <button onClick={() => setShowForm(false)} className="px-3 py-1.5 border border-border text-xs text-muted-foreground rounded-sm hover:text-foreground transition-colors">Cancel</button>
              <button onClick={handleSubmit} disabled={submitting || !form.occurrence_type || !form.fatigue_level || !form.description}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-bold rounded-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
                data-testid="submit-occurrence-btn">
                <Send size={10} /> {submitting ? "Submitting..." : "Submit Report"}
              </button>
            </div>
          </div>
        </div>
      )}

      {occurrences.length === 0 && !showForm && (
        <div className="text-center py-10 text-muted-foreground text-xs">No occurrence reports filed yet. Click "File Report" to submit a confidential fatigue event.</div>
      )}

      <div className="space-y-2">
        {occurrences.map(occ => (
          <div key={occ.id} className="bg-card border border-border rounded-sm p-4" data-testid={`occurrence-${occ.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-bold text-primary">{occ.ref_number}</span>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 border rounded-sm ${STATUS_CFG[occ.status]?.cls || ""}`}>
                    {STATUS_CFG[occ.status]?.label || occ.status?.toUpperCase()}
                  </span>
                </div>
                <div className="text-sm font-medium text-foreground mt-1">{occ.occurrence_type}</div>
                <div className="text-xs text-muted-foreground mt-1">{occ.description}</div>
              </div>
              <div className="text-right shrink-0 ml-4 text-[10px] text-muted-foreground">
                <div>{occ.occurrence_date}</div>
                <div>{occ.fatigue_level}</div>
                {occ.flight_number && <div className="font-mono">{occ.flight_number}</div>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Scientific Evidence Tab ───────────────────────────────────────────────────
function ScienceTab({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-5">
      <div className="bg-card border border-border rounded-sm p-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {[
            { label: "Model",            value: data.model?.split(" ")[0] || "SAFE v4.2" },
            { label: "Crew Tracked",     value: data.total_crew_tracked },
            { label: "Flights Analysed", value: data.total_flights_analysed?.toLocaleString() },
            { label: "Data Period",      value: data.data_period },
          ].map(({ label, value }) => (
            <div key={label}>
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</div>
              <div className="text-sm font-bold text-foreground mt-0.5">{value}</div>
            </div>
          ))}
        </div>
        <div className="text-[10px] text-muted-foreground">Model: {data.model} · Validated: {data.validation_date}</div>
      </div>

      <div className="bg-card border border-border rounded-sm p-4">
        <div className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-3">Key Scientific Findings</div>
        <div className="space-y-2">
          {(data.key_findings || []).map((f, i) => (
            <div key={i} className="flex items-start gap-2">
              <CheckCircle size={12} className="text-green-400 shrink-0 mt-0.5" />
              <span className="text-xs text-foreground">{f}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {(data.bio_model_data || []).map(cat => (
          <div key={cat.crew_category} className="bg-card border border-border rounded-sm p-4">
            <div className="text-xs font-bold text-foreground mb-2">{cat.crew_category}</div>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <div><span className="text-muted-foreground">Sample: </span><span className="font-mono text-foreground">{cat.sample_size}</span></div>
              <div><span className="text-muted-foreground">Alertness: </span><span className="font-mono text-primary">{cat.avg_alertness_score}</span></div>
              <div><span className="text-muted-foreground">Sleep: </span><span className="font-mono text-foreground">{cat.sleep_opportunity_hours}h</span></div>
              <div><span className="text-muted-foreground">WOCL: </span><span className="font-mono text-amber-400">{cat.wocl_exposure_pct}%</span></div>
              <div className="col-span-2"><span className="text-muted-foreground">Peak Fatigue: </span><span className="font-mono text-red-400">{cat.peak_fatigue_window}</span></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Audit Tab ────────────────────────────────────────────────────────────────
function AuditTab({ audits }) {
  const STATUS_CFG = {
    closed:      { cls: "bg-green-500/10 text-green-400 border-green-500/20" },
    in_progress: { cls: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
    open:        { cls: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  };
  return (
    <div className="space-y-3">
      {audits.map(a => (
        <div key={a.id} className="bg-card border border-border rounded-sm p-5" data-testid={`audit-${a.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-xs font-bold text-muted-foreground">{a.id}</span>
                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-primary/10 text-primary border border-primary/20 rounded-sm">{a.type}</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 border rounded-sm ${STATUS_CFG[a.status]?.cls}`}>{a.status.replace("_"," ").toUpperCase()}</span>
              </div>
              <div className="text-sm font-bold text-foreground">{a.title}</div>
              <div className="text-xs text-muted-foreground mt-1">{a.scope}</div>
              <div className="text-xs text-muted-foreground mt-1">Auditor: {a.auditor}</div>
            </div>
            <div className="shrink-0 text-right">
              <div className="text-xs font-mono text-muted-foreground">{a.start_date} → {a.end_date}</div>
              <div className="mt-2 flex gap-2 justify-end">
                {a.critical > 0 && <span className="text-[10px] font-bold bg-red-500/10 text-red-400 border border-red-500/20 px-1.5 py-0.5 rounded-sm">{a.critical} Critical</span>}
                {a.major > 0 && <span className="text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 px-1.5 py-0.5 rounded-sm">{a.major} Major</span>}
                {a.minor > 0 && <span className="text-[10px] font-bold bg-blue-500/10 text-blue-400 border border-blue-500/20 px-1.5 py-0.5 rounded-sm">{a.minor} Minor</span>}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Rating: <span className="font-bold text-foreground">{a.overall_rating}</span></div>
              {a.corrective_actions_open > 0 && (
                <div className="text-[10px] text-amber-400 mt-1">{a.corrective_actions_open} open actions</div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
const TABS = [
  { id: "overview",  label: "Overview",            Icon: TrendingUp },
  { id: "policy",    label: "Policy Library",       Icon: BookOpen },
  { id: "hazards",   label: "Hazard Register",      Icon: AlertTriangle },
  { id: "reports",   label: "Occurrence Reports",   Icon: FileSearch },
  { id: "audit",     label: "Audit Trail",          Icon: FileText },
  { id: "science",   label: "Scientific Evidence",  Icon: FlaskConical },
  { id: "sfr",       label: "SFR Live Feed",        Icon: Radio },
];

export default function FRMSModule() {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [policyDocs, setPolicyDocs] = useState([]);
  const [hazards, setHazards] = useState({ hazards: [], summary: {} });
  const [occurrences, setOccurrences] = useState([]);
  const [audits, setAudits] = useState([]);
  const [science, setScience] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, pol, haz, occ, aud, sci] = await Promise.allSettled([
        apiFetch("/overview"),
        apiFetch("/policy-docs"),
        apiFetch("/hazard-register"),
        apiFetch("/occurrences"),
        apiFetch("/audits"),
        apiFetch("/scientific-evidence"),
      ]);
      if (ov.status === "fulfilled") setOverview(ov.value);
      if (pol.status === "fulfilled") setPolicyDocs(pol.value.documents || []);
      if (haz.status === "fulfilled") setHazards(haz.value);
      if (occ.status === "fulfilled") setOccurrences(occ.value.occurrences || []);
      if (aud.status === "fulfilled") setAudits(aud.value.audits || []);
      if (sci.status === "fulfilled") setScience(sci.value);
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const reloadOccurrences = async () => {
    const d = await apiFetch("/occurrences");
    setOccurrences(d.occurrences || []);
    const ov = await apiFetch("/overview");
    setOverview(ov);
  };

  if (loading) return (
    <div className="flex-1 p-6 flex items-center justify-center">
      <div className="text-primary font-mono text-sm animate-pulse">LOADING FRMS MODULE...</div>
    </div>
  );

  return (
    <div className="p-6 space-y-5" data-testid="frms-module">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-foreground tracking-tight">FRMS — Phase 3</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Fatigue Risk Management System · ICAO Doc 9966 · DGCA CAR Section 7
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-sm">
          <Shield size={13} className="text-green-400" />
          <span className="text-xs font-bold text-green-400">{overview?.compliance_score}% Compliance — {overview?.dgca_readiness}</span>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {TABS.map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold whitespace-nowrap border-b-2 transition-colors ${
              tab === id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`frms-tab-${id}`}>
            <Icon size={11} /> {label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab overview={overview} onSelectTab={setTab} />}
      {tab === "policy"   && <PolicyTab docs={policyDocs} />}
      {tab === "hazards"  && <HazardTab data={hazards} />}
      {tab === "reports"  && <OccurrenceTab occurrences={occurrences} onSubmitted={reloadOccurrences} />}
      {tab === "audit"    && <AuditTab audits={audits} />}
      {tab === "science"  && <ScienceTab data={science} />}
      {tab === "sfr"      && <SFRDashboard />}
    </div>
  );
}
